import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { useAppStore } from "@/store";

type Document = components["schemas"]["Document"];
type DocumentListResponse = components["schemas"]["DocumentListResponse"];
type DocumentSource = components["schemas"]["DocumentSource"];
type DocumentNodeType = components["schemas"]["DocumentNodeType"];

export type { Document, DocumentListResponse, DocumentSource, DocumentNodeType };

/** Sort columns the listDocuments server-side allow-list accepts. */
export type DocumentSortBy = "created_at" | "filename" | "size_bytes" | "source";

/** Query parameters for the org-wide document list (the repository table). */
export interface DocumentRepoParams {
  /** Roll-up axis (paired with node_id). Omit both for the org-wide set. */
  node_type?: DocumentNodeType;
  node_id?: string;
  /** Case-insensitive filename substring search. */
  search?: string;
  /** Restrict to one or more sources (OR within the set). */
  source?: DocumentSource[];
  /** Exact content_type match (e.g. application/pdf). */
  type?: string;
  created_after?: string;
  created_before?: string;
  sort_by?: DocumentSortBy;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

// The document set is read-only in the repository; a 2-minute stale window
// keeps page-flips and tree-node changes snappy without hammering the API.
const LIST_STALE_TIME = 2 * 60_000;
const LIST_GC_TIME = 10 * 60_000;

/**
 * Query-key factory for the repository list cache. Keyed on orgId + the full
 * filter/sort/page param set so each distinct view caches independently and a
 * source-aware delete can invalidate the whole list prefix.
 */
export const documentRepoKeys = {
  all: ["document-repo"] as const,
  lists: (orgId: string) => [...documentRepoKeys.all, "list", orgId] as const,
  list: (orgId: string, params: DocumentRepoParams) =>
    [...documentRepoKeys.lists(orgId), params] as const,
};

/**
 * The org-wide rolled-up document list for the repository table. When a tree
 * node is selected, node_type + node_id scope the page to that node's subtree;
 * otherwise the org-wide set is returned. Server-paged + server-sorted +
 * server-filtered (NO NAKED TABLES). placeholderData keeps the prior page
 * visible while the next loads.
 */
export function useDocumentRepo(params: DocumentRepoParams) {
  const orgId = useAppStore((s) => s.activeOrgId);
  return useQuery<DocumentListResponse>({
    queryKey: documentRepoKeys.list(orgId ?? "", params),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.GET("/orgs/{orgId}/documents", {
        params: {
          path: { orgId },
          query: {
            node_type: params.node_type,
            node_id: params.node_id,
            search: params.search,
            source: params.source,
            type: params.type,
            created_after: params.created_after,
            created_before: params.created_before,
            sort_by: params.sort_by,
            sort_dir: params.sort_dir,
            limit: params.limit,
            offset: params.offset,
          },
        },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load documents";
        throw new ApiError(response.status, msg, (error as { code?: string }).code, error);
      }
      return data;
    },
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
  });
}
